import { Grid, Card, CardHeader, CardContent, Button, Typography } from '@mui/material';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { contactApiKeyStore } from '../../../api/contact-api-key-store';
import { useEffect, useState } from 'react';
import { ContactsDialog } from '../../contacts/contacts.dialog';
import { Contact } from '../../../models/contact';
import { useFormContext } from 'react-hook-form';
import { customersApiKeyStore } from '../../../api/customers-api-key-store';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { columns } from '../../contacts/grid-columns';
import { ContactsDialogForCustomer } from './contact-dialog-for-customer';
import { ContactsDialogWithGridSelect } from '../../contacts/contact-diallog-with-grid-selection';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useSaveData } from '../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';
import { LoadingIndicator } from '../../../components/loading-indicator.component';

interface ContactsFormProps {
  name: string;
  label: string;
}

export const ContactsForm = ({ name, label }: ContactsFormProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { setValue, getValues } = useFormContext();
  const [valueForTab, setValueForTab] = useState('view');
  const [editItem, setEditItem] = useState<Contact>();

  const handleOpenAddContactForm = () => {
    setOpenDialog(true);
  };

  const handleEdit = (value: Contact) => {
    setEditItem(value);
    setValueForTab('edit');
    handleOpenAddContactForm();
  };

  const handleCloseAddContactForm = (contacts: Contact[]) => {
    setOpenDialog(false);
    setValue(name, contacts);
    setEditItem(undefined);
  };

  const handleAddContactFormWithOutClose = (contacts: Contact[]) => {
    setValue(name, contacts);
    setEditItem(undefined);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Customer', 'Contact'));

  // const { isLoading, data: Offices, refetch } = useLoadData<ApiResponse<Contact[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredContactList = (getValues(name) || []).filter((contact: any) =>
      Object.values(contact).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredContacts(filteredContactList || []);
  }, [debouncedSearchTerm, getValues(name)]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Contact',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Contact',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary">
            CONTACTS
          </Typography>
        }
        action={
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
            <Button
              className="btnLink"
              variant="text"
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => handleOpenAddContactForm()}
            >
              Add Contacts
            </Button>
          </GuardedComponent>
        }
      ></CardHeader>
      <CardContent className="formContainer">
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            {/* <FormAutoCompleteMultiSelectDynamic
              name={name}
              label={label}
              dataItems={getValues(name)}
              getQueryKeyConfig={() => contactApiKeyStore.getCustomerContacts(getValues('customerID'))}
              optionsLabelKey="contactsFirstName"
              optionsValueKey="contactsID"
            /> */}
            <LoadingIndicator isLoading={isLoadingGridLayout} />
            <SiTrackGrid
              fileTitle='customer_contact'
              // dataItems={getValues(name)}
              dataItemKey="contactsID"
              columns={columns}
              actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              // -----------------Grid Layout-------
              dataItems={filteredContacts || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            // onEdit={handleEdit}
            // deleteMutationConfig={(item) => contactApiKeyStore.deleteContact(item.contactsID)}
            />
          </Grid>
        </Grid>
        {openDialog && (
          <ContactsDialogWithGridSelect
            open={openDialog}
            onClose={handleCloseAddContactForm}
            contacts={getValues(name)}
            customerID={getValues('customer.customerID')}
            setValue={setValueForTab}
            isSyncWithDux={getValues('syncWithDux')}
            value={valueForTab}
            editItem={editItem}
            setEditItem={setEditItem}
            watchKeys={['customer.customerID']}
            getQueryKeyConfig={() => contactApiKeyStore.getCustomerContacts(getValues('customerID'))}
            page="customer"
            handleAddContactFormWithOutClose={handleAddContactFormWithOutClose}
          />
        )}
      </CardContent>
    </Card>
  );
};
